// The one skin every prerendered marketing page wears: nav + footer + the SPLASH_NAV link graph + the frozen CTA literals.
// Named exports only, NO default export — a file under app/routes/ is a route only if app/routes.ts names it, and a default export invites someone to wire it up as one.
// Node-prerender-safe (the whole graph is eagerly imported during prerender): MUI + react-router + `Setup/Mui` + `Static/FooterParts` only. AppBar.tsx cannot be reused — it imports `../../data/store` at module scope and reads `useStore(s => s.authenticated)` to pick the logo target — and this nav is a different component anyway (SPLASH_NAV + auth CTAs, no in-app CTAs/back-button/menus). The FOOTER is only half-mirrored: its Connect column needs `utils/config.ts`'s `COMMUNITY_LINK`, which drags Amplify/env config into the graph, and its middle column differs from the SPA's by design — everything that does NOT differ lives in `Static/FooterParts.tsx` and is imported by both.
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import MUILink from '@mui/material/Link';
import EmailIcon from '@mui/icons-material/MailOutlined';
import Mui from '../../src/ui/Setup/Mui';
import { FooterBrand, FooterCopyright, FooterHeading, FooterLegal, FooterLink } from '../../src/ui/Static/FooterParts';

/** The physical anchor of the cross-page link graph: the nav, the footer's Product column and the homepage's cross-link block all read THIS. Hand-rolling the list anywhere else is a bug. */
export const SPLASH_NAV = [
  { key: 'series', label: 'Series', to: '/series' },
  { key: 'fields', label: 'Fields', to: '/fields' },
  { key: 'community', label: 'Community', to: '/community' },
  { key: 'publishing', label: 'Publishing', to: '/publishing' },
] as const;

export type SplashPage = 'home' | (typeof SPLASH_NAV)[number]['key'];

/** Frozen strings — the SPA /auth landing reads the `tab` param. Defined once so six pages cannot drift; never retype either URL. */
export const AUTH_SIGN_UP = '/auth?tab=signUp';
export const AUTH_SIGN_IN = '/auth?tab=signIn';

/** Sticky-nav clearance. Every section's `scrollMarginTop` references this, so a nav-height change can't silently drop `#tts` and friends under the bar. */
export const NAV_OFFSET = '80px';

function Logo() {
  // Always `/` — the authed-vs-anon logo target is exactly the `authenticated` store read this module cannot have.
  return (
    <MUILink component={RouterLink} to="/" underline="none" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="img" src="/gnothi_logo.png" alt="Gnothi" sx={{ height: 30, width: 30, borderRadius: '6px', objectFit: 'contain' }} />
      {/* `component="span"`: a logo is not a document heading, and `variant="h6"` alone emits a real <h6> — every page was shipping this one plus the footer's above its own <h2>s. The `variant` keeps the type scale. */}
      <Typography variant="h6" component="span" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: '1.15rem', color: 'primary.main', letterSpacing: '0.02em', lineHeight: 1 }}>
        Gnothi
      </Typography>
    </MUILink>
  );
}

export function SplashNav({ active }: { active?: SplashPage }) {
  // Starts closed on both sides of hydration, so local state is safe here where a store read would not be.
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 56 }}>
        <Logo />
        <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: 'center', display: { xs: 'none', md: 'flex' } }}>
          {SPLASH_NAV.map((item) => (
            <Button
              key={item.key}
              component={RouterLink}
              to={item.to}
              color="inherit"
              sx={{
                fontWeight: active === item.key ? 700 : 500,
                borderRadius: 0,
                borderBottom: '2px solid',
                borderColor: active === item.key ? 'splash.accent' : 'transparent',
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
        <Box sx={{ flex: 1, display: { xs: 'block', md: 'none' } }} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          <Button component={RouterLink} to={AUTH_SIGN_IN} color="inherit" sx={{ whiteSpace: 'nowrap' }}>Sign in</Button>
          <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" sx={{ whiteSpace: 'nowrap' }}>Get started free</Button>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {/* `splash.accent`, not `primary.main` — same reason as the active-tab rule above, which is the ONLY visual marker of which page you are on: an interactive control or a state indicator owes 3:1 non-text contrast and `#00A8E8` is 2.6:1 on this `background.default` bar. (The wordmark keeps `primary.main` — a logotype is exempt.) */}
            <IconButton aria-label="site menu" aria-haspopup="true" onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: 'splash.accent' }}>
              <MenuIcon />
            </IconButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} disableScrollLock>
              {SPLASH_NAV.map((item) => (
                <MenuItem key={item.key} component={RouterLink} to={item.to} onClick={close} selected={active === item.key}>
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export function SplashFooter() {
  // background.elevated is a dark navy/slate surface in BOTH schemes, so text is always common.white — primary.contrastText flips to dark navy in the dark scheme (dark-on-dark, unreadable).
  return (
    <Box component="footer" sx={{ bgcolor: 'background.elevated', color: 'common.white', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Grid size={{ xs: 12, md: 5 }}><FooterBrand /></Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FooterHeading>Product</FooterHeading>
            {SPLASH_NAV.map((item) => <FooterLink key={item.key} to={item.to}>{item.label}</FooterLink>)}
            <FooterLink external to="mailto:gnothi@gnothiai.com" icon={<EmailIcon fontSize="small" />}>Email Us</FooterLink>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}><FooterLegal /></Grid>
        </Grid>

        {/* Marketing pages only (the SPA footer is inside the product, where the full Disclaimer is an accepted ack). One quiet line, no crisis numbers: those live on /disclaimer and in-app. Wording per the FTC language packet §5, minus "psychological" so the clinical-stem gate needs no exemption. */}
        <Typography variant="body2" sx={{ mt: 4, opacity: 0.6 }}>
          Gnothi is a writing, tracking and storytelling tool. It doesn't give medical or professional advice.{' '}
          <MUILink color="inherit" underline="always" component={RouterLink} to="/disclaimer">Read the disclaimer</MUILink>
        </Typography>

        <FooterCopyright />
      </Container>
    </Box>
  );
}

/** The one thing a marketing page renders. Provides the theme (never nest a second `Mui`), the sticky nav, and a footer pinned to the bottom of a short page. */
export function SplashChrome({ children, active }: { children: ReactNode; active?: SplashPage }) {
  return (
    <Mui>
      <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
        <SplashNav active={active} />
        <Box component="main" sx={{ flex: 1 }}>{children}</Box>
        <SplashFooter />
      </Box>
    </Mui>
  );
}
